import { getWordsCount } from '~/helpers/functions';
import {
  DETECTION_EDGE,
  DETECTION_STAGE,
} from '~/modules/tools/views/AiDetectorV4/constants';
import { getPlagiarismLoadingTime } from '~/modules/tools/views/PlagiarismCheckerV4/helpers';

export const getDetectionStage = (result: number): DETECTION_STAGE => {
  const stage = Object.entries(DETECTION_EDGE)
    .sort(([, a], [, b]) => b - a)
    .find(([, value]) => result >= value);
  return stage ? (stage[0] as DETECTION_STAGE) : DETECTION_STAGE.FULLY_HUMAN;
};

export const getTextSpecialSymbols = (text: string): string[] => {
  const regexp = /[*#]/g;
  const matches = text.match(regexp);

  if (!matches) {
    return [];
  }

  return [...new Set(matches)];
};

type LoadingConfig = {
  response_coefficient: number;
  edge_coefficient: number;
  min_time?: {
    symbol_threshold: number;
    time: number;
  }; // ms
  max_time?: {
    symbol_threshold: number;
    time: number;
  }; // ms
};

enum RequestLang {
  ENGLISH = 'eng',
  NON_ENGLISH = 'non-eng',
}

export const getDetectorLoadingTime = (
  requestText: string,
  requestLang: string,
): number => {
  const loadingConfigs: Record<RequestLang, LoadingConfig> = {
    [RequestLang.ENGLISH]: {
      response_coefficient: 0.00107,
      edge_coefficient: 1.82,
      max_time: {
        symbol_threshold: 50000,
        time: 55000,
      }, //ms
    },
    [RequestLang.NON_ENGLISH]: {
      response_coefficient: 0.0213,
      edge_coefficient: 1.6 * -1,
      min_time: {
        symbol_threshold: 300,
        time: 4000,
      }, //ms
      max_time: {
        symbol_threshold: 25000,
        time: 550000,
      }, //ms
    },
  };

  const requestLangType =
    requestLang === RequestLang.ENGLISH
      ? RequestLang.ENGLISH
      : RequestLang.NON_ENGLISH;

  const config =
    loadingConfigs[requestLangType] || loadingConfigs[RequestLang.ENGLISH];

  const wordsCount = getWordsCount(requestText);

  if (config.max_time && wordsCount > config.max_time.symbol_threshold) {
    return config.max_time.time;
  }

  if (config.min_time && wordsCount < config.min_time.symbol_threshold) {
    return config.min_time.time;
  }

  return (
    Math.round(
      config.response_coefficient * wordsCount + config.edge_coefficient,
    ) * 1000
  );
};

export const calculateDetectorLoadingTime = (data: {
  requestText: string;
  requestLang: string;
  isPlagiarismIncluded: boolean;
}): number => {
  const detectorTime = getDetectorLoadingTime(
    data.requestText,
    data.requestLang,
  );

  if (data.isPlagiarismIncluded) {
    const plagiarismTime = getPlagiarismLoadingTime(
      getWordsCount(data.requestText),
    );

    return Math.max(detectorTime, plagiarismTime);
  }

  return detectorTime;
};

export const getDetectorWordsBalance = (): number => {
  const BALANCE = {
    standard: 80000,
  };

  return BALANCE.standard;
};

export const getDetectorInputLimits = (data: {
  isNotPremium: boolean;
}): { minCount: number; maxCount: number } => {
  const LIMITS = {
    standard_min: 50,
    standard_max: 50000,
    not_premium_max: 25000,
  };

  return {
    minCount: LIMITS.standard_min,
    maxCount: data.isNotPremium ? LIMITS.not_premium_max : LIMITS.standard_max,
  };
};
